<script lang="ts" setup>
import { nextTick, ref, watch } from 'vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { VIconButtonVariants } from '@/ui/plushComponents/VIconButton/types'

const props = withDefaults(defineProps<{ show: boolean }>(), {
    show: false,
})
const emit = defineEmits<{ (e: EEmits.TURN_OFF): void }>()

const bubble = ref()
const { activate, deactivate } = useFocusTrap(bubble)

enum EEmits {
    TURN_OFF = 'turnOff',
}

function turnOff(): void {
    emit(EEmits.TURN_OFF)
    deactivate()
}

watch(
    () => props.show,
    value => {
        if (value) {
            return nextTick(() => activate())
        }
        turnOff()
    },
)
</script>

<template>
    <div
        ref="bubble"
        class="bubble relative"
    >
        <div>
            <!-- TRIGGER -->
            <slot name="trigger" />
        </div>

        <div
            v-if="show"
            class="fixed w-[48px]"
            @keydown.esc="turnOff"
        >
            <div
                class="shadow-default-below p-md gap-xs border-red rounded-container-md bg-comp-primary-light text-neutral-primary max-[360px]:inset-x-base absolute left-1/2 top-[20px] flex flex-col max-[360px]:fixed max-[360px]:top-[80px] min-[361px]:w-[328px] min-[361px]:-translate-x-1/2 md:right-0"
            >
                <div>
                    <div class="relative">
                        <div
                            class="text-neutral-primary bold-paragraph-lg line-clamp-1 pr-8"
                            tabindex="1"
                        >
                            <slot name="title" />
                        </div>

                        <VIconButton
                            tabindex="3"
                            icon="close"
                            :variant="VIconButtonVariants.plain"
                            size="MEDIUM"
                            class="absolute -top-2 right-0"
                            data-test="header-close-bubble"
                            @click="turnOff"
                        />
                    </div>

                    <div
                        tabindex="2"
                        class="bubble__content mt-xs"
                    >
                        <slot name="content" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.bubble__content a {
  text-decoration: underline;
}
</style>
